import React, { useEffect, useMemo, useState } from "react";
import {
  Button,
  Checkbox,
  CircularProgress,
  Divider,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography
} from "@material-ui/core";
import { toast } from "react-toastify";

const initialFormState = {
  name: "",
  message: "",
  messageVariations: Array.from({ length: 9 }, () => ""),
  scheduledFor: "",
  whatsappIds: [],
  contactIds: [],
  tagId: "",
  queueId: ""
};

const normalizeHeader = value =>
  String(value || "")
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .replace(/\s+/g, "")
    .toLowerCase();

const detectDelimiter = headerLine => {
  if (headerLine.includes(";")) return ";";
  if (headerLine.includes("\t")) return "\t";
  return ",";
};

const parseSpreadsheetText = rawText => {
  const lines = String(rawText || "")
    .split(/\r?\n/)
    .map(line => line.trim())
    .filter(Boolean);

  if (!lines.length) {
    return { entries: [], error: "Planilha vazia." };
  }

  const delimiter = detectDelimiter(lines[0]);
  const headers = lines[0].split(delimiter).map(item => item.trim());
  const normalizedHeaders = headers.map(normalizeHeader);
  const cnpjIndex = normalizedHeaders.indexOf("cnpj");
  const razaoIndex = normalizedHeaders.findIndex(header =>
    ["razao", "razaosocial"].includes(header)
  );
  const telefoneIndex = normalizedHeaders.findIndex(header =>
    ["telefone", "telefone1", "celular", "fone", "numero"].includes(header)
  );

  if (cnpjIndex === -1 || razaoIndex === -1 || telefoneIndex === -1) {
    return {
      entries: [],
      error: "Colunas obrigatórias: CNPJ, Razão e Telefone."
    };
  }

  const entries = lines
    .slice(1)
    .map(line => line.split(delimiter).map(item => item.trim()))
    .map(parts => ({
      cnpj: String(parts[cnpjIndex] || "").replace(/\D/g, ""),
      razao: String(parts[razaoIndex] || "").trim(),
      telefone: String(parts[telefoneIndex] || "").replace(/\D/g, "")
    }))
    .filter(entry => entry.telefone.length >= 10 && entry.telefone.length <= 15);

  const unique = [];
  const seenPhones = new Set();
  entries.forEach(entry => {
    if (seenPhones.has(entry.telefone)) return;
    seenPhones.add(entry.telefone);
    unique.push(entry);
  });

  return { entries: unique, error: "" };
};

const ScheduledBroadcastModal = ({
  open,
  onClose,
  onSubmit,
  loading,
  whatsApps,
  tags,
  contacts,
  queues
}) => {
  const [form, setForm] = useState(initialFormState);
  const [spreadsheetName, setSpreadsheetName] = useState("");
  const [spreadsheetEntries, setSpreadsheetEntries] = useState([]);

  useEffect(() => {
    if (!open) {
      setForm(initialFormState);
      setSpreadsheetName("");
      setSpreadsheetEntries([]);
    }
  }, [open]);

  const updateFormField = field => event => {
    const value = event && event.target ? event.target.value : "";
    setForm(prev => ({ ...prev, [field]: value }));
  };

  const updateVariationField = index => event => {
    const value = event && event.target ? event.target.value : "";
    setForm(prev => {
      const nextVariations = [...(prev.messageVariations || [])];
      nextVariations[index] = value;
      return { ...prev, messageVariations: nextVariations };
    });
  };

  const handleContactsChange = event => {
    const rawValue = event && event.target ? event.target.value : [];
    const normalizedValue = (Array.isArray(rawValue) ? rawValue : [])
      .map(item => Number(item))
      .filter(item => Number.isInteger(item) && item > 0);

    setForm(prev => ({ ...prev, contactIds: normalizedValue }));
  };

  const handleWhatsAppsChange = event => {
    const rawValue = event && event.target ? event.target.value : [];
    const normalizedValue = (Array.isArray(rawValue) ? rawValue : [])
      .map(item => Number(item))
      .filter(item => Number.isInteger(item) && item > 0);

    setForm(prev => ({ ...prev, whatsappIds: normalizedValue }));
  };

  const handleSpreadsheetUpload = event => {
    const file = event?.target?.files?.[0];
    if (!file) {
      return;
    }

    const reader = new FileReader();
    reader.onload = loadEvent => {
      const text = loadEvent?.target?.result;
      const { entries, error } = parseSpreadsheetText(text);
      setSpreadsheetEntries(entries);
      setSpreadsheetName(file.name);

      if (error) {
        toast.error(error);
      } else if (!entries.length) {
        toast.error("Não foi possível identificar contatos válidos na planilha.");
      } else {
        toast.success(`${entries.length} contato(s) carregado(s) da planilha.`);
      }
    };
    reader.onerror = () => {
      toast.error("Falha ao ler a planilha.");
    };

    reader.readAsText(file, "utf-8");
  };

  const selectedContactsLabel = useMemo(() => {
    if (!form.contactIds.length) return "Nenhum contato selecionado";
    return `${form.contactIds.length} contato(s) selecionado(s)`;
  }, [form.contactIds.length]);

  const selectedWhatsAppsLabel = useMemo(() => {
    if (!form.whatsappIds.length) return "Nenhuma instância selecionada";
    return `${form.whatsappIds.length} instância(s) selecionada(s)`;
  }, [form.whatsappIds.length]);

  const activeWhatsApps = useMemo(() => {
    return (whatsApps || []).filter(item => item.status !== "DISCONNECTED");
  }, [whatsApps]);

  const totalRecipients = form.contactIds.length + spreadsheetEntries.length;
  const variationsCount =
    (form.messageVariations || []).filter(item => String(item || "").trim()).length +
    (form.message.trim() ? 1 : 0);
  const estimatedMinutes = form.whatsappIds.length
    ? Math.ceil(totalRecipients / Math.max(form.whatsappIds.length, 1))
    : 0;
  const messagePreview = form.message
    .replace(/\{\{\s*Raz[aã]o\s*\}\}/gi, "Empresa Exemplo")
    .replace(/\{\{\s*Telefone\s*\}\}/gi, "5511999999999")
    .replace(/\{\{\s*CNPJ\s*\}\}/gi, "00000000000100");

  const handleSubmit = async event => {
    event.preventDefault();

    if (!form.message.trim()) {
      toast.error("Informe a mensagem.");
      return;
    }

    if (!form.scheduledFor) {
      toast.error("Informe a data e hora do agendamento.");
      return;
    }

    if (!form.whatsappIds.length) {
      toast.error("Selecione ao menos uma instância.");
      return;
    }

    if (!form.tagId) {
      toast.error("Selecione uma etiqueta para cada conversa.");
      return;
    }

    if (!form.contactIds.length && !spreadsheetEntries.length) {
      toast.error("Selecione contatos ou envie uma planilha.");
      return;
    }

    await onSubmit({
      ...form,
      whatsappIds: form.whatsappIds,
      messageVariations: (form.messageVariations || [])
        .map(item => String(item || "").trim())
        .filter(Boolean),
      tagId: Number(form.tagId),
      queueId: form.queueId ? Number(form.queueId) : undefined,
      contactEntries: spreadsheetEntries
    });
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle>Novo Agendamento em Massa</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent dividers>
          <Paper variant="outlined" style={{ padding: 12, marginBottom: 16 }}>
            <Grid container spacing={2}>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  Destinatários
                </Typography>
                <Typography variant="h6">{totalRecipients}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  Instâncias
                </Typography>
                <Typography variant="h6">{form.whatsappIds.length}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  Variações
                </Typography>
                <Typography variant="h6">{variationsCount}</Typography>
              </Grid>
              <Grid item xs={6} md={3}>
                <Typography variant="caption" color="textSecondary">
                  Estimativa
                </Typography>
                <Typography variant="h6">
                  {estimatedMinutes ? `${estimatedMinutes}+ min` : "-"}
                </Typography>
              </Grid>
            </Grid>
          </Paper>

          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Configuração da campanha
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={6}>
              <TextField
                label="Nome do agendamento"
                variant="outlined"
                fullWidth
                margin="dense"
                value={form.name}
                onChange={updateFormField("name")}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <TextField
                label="Data e hora"
                type="datetime-local"
                variant="outlined"
                fullWidth
                margin="dense"
                InputLabelProps={{ shrink: true }}
                value={form.scheduledFor}
                onChange={updateFormField("scheduledFor")}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth margin="dense">
                <InputLabel id="scheduled-broadcast-whatsapp-label">
                  Instâncias
                </InputLabel>
                <Select
                  labelId="scheduled-broadcast-whatsapp-label"
                  multiple
                  value={form.whatsappIds}
                  onChange={handleWhatsAppsChange}
                  label="Instâncias"
                  renderValue={() => selectedWhatsAppsLabel}
                >
                  {activeWhatsApps.map(whatsApp => (
                    <MenuItem key={whatsApp.id} value={whatsApp.id}>
                      <Checkbox checked={form.whatsappIds.indexOf(whatsApp.id) > -1} />
                      <ListItemText primary={whatsApp.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth margin="dense">
                <InputLabel id="scheduled-broadcast-tag-label">Etiqueta</InputLabel>
                <Select
                  labelId="scheduled-broadcast-tag-label"
                  value={form.tagId}
                  onChange={updateFormField("tagId")}
                  label="Etiqueta"
                >
                  {tags.map(tag => (
                    <MenuItem
                      key={tag.id}
                      value={String(tag.id)}
                      disabled={tag.isGroup}
                    >
                      {tag.isGroup
                        ? tag.name
                        : `${tag.parent?.name ? `${tag.parent.name} / ` : ""}${tag.name}`}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl variant="outlined" fullWidth margin="dense">
                <InputLabel id="scheduled-broadcast-queue-label">Fila</InputLabel>
                <Select
                  labelId="scheduled-broadcast-queue-label"
                  value={form.queueId}
                  onChange={updateFormField("queueId")}
                  label="Fila"
                >
                  <MenuItem value="">
                    <em>Sem fila</em>
                  </MenuItem>
                  {(queues || []).map(queue => (
                    <MenuItem key={queue.id} value={String(queue.id)}>
                      {queue.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          <Divider style={{ margin: "16px 0" }} />

          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Mensagem template
          </Typography>
          <TextField
            label="Mensagem principal"
            variant="outlined"
            multiline
            rows={5}
            fullWidth
            margin="dense"
            value={form.message}
            onChange={updateFormField("message")}
            helperText="Use placeholders como {{Razão}}, {{Telefone}} e {{CNPJ}}."
          />
          {form.message.trim() && (
            <Paper
              variant="outlined"
              style={{
                padding: 12,
                marginTop: 8,
                backgroundColor: "#f7f9fc",
                whiteSpace: "pre-wrap"
              }}
            >
              <Typography variant="caption" color="textSecondary">
                Preview
              </Typography>
              <Typography variant="body2">{messagePreview}</Typography>
            </Paper>
          )}

          <Typography variant="subtitle2" style={{ marginTop: 12, marginBottom: 8 }}>
            Variações manuais (opcional)
          </Typography>
          <Grid container spacing={2}>
            {(form.messageVariations || []).map((variation, index) => (
              <Grid item xs={12} md={6} key={`message-variation-${index}`}>
                <TextField
                  label={`Variação ${index + 1}`}
                  variant="outlined"
                  multiline
                  rows={3}
                  fullWidth
                  margin="dense"
                  value={variation}
                  onChange={updateVariationField(index)}
                />
              </Grid>
            ))}
          </Grid>

          <Divider style={{ margin: "16px 0" }} />

          <Typography variant="subtitle2" style={{ marginBottom: 8 }}>
            Destinatários
          </Typography>

          <FormControl variant="outlined" fullWidth margin="dense">
            <InputLabel id="scheduled-broadcast-contacts-label">
              Contatos
            </InputLabel>
            <Select
              labelId="scheduled-broadcast-contacts-label"
              multiple
              value={form.contactIds}
              onChange={handleContactsChange}
              label="Contatos"
              renderValue={() => selectedContactsLabel}
            >
              {contacts.map(contact => (
                <MenuItem key={contact.id} value={contact.id}>
                  <Checkbox checked={form.contactIds.indexOf(contact.id) > -1} />
                  <ListItemText primary={`${contact.name} (${contact.number})`} />
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <div style={{ marginTop: 8 }}>
            <input
              accept=".csv,.txt"
              style={{ display: "none" }}
              id="scheduled-broadcast-spreadsheet-input"
              type="file"
              onChange={handleSpreadsheetUpload}
            />
            <label htmlFor="scheduled-broadcast-spreadsheet-input">
              <Button variant="outlined" component="span">
                Enviar planilha (CSV/TXT)
              </Button>
            </label>
            <div style={{ marginTop: 6, fontSize: 12, color: "rgba(0,0,0,0.65)" }}>
              {spreadsheetName
                ? `${spreadsheetName} - ${spreadsheetEntries.length} contato(s) válidos`
                : "Formato com colunas: CNPJ, Razão, Telefone"}
            </div>
          </div>
        </DialogContent>

        <DialogActions>
          <Button onClick={onClose} color="secondary" disabled={loading}>
            Cancelar
          </Button>
          <Button type="submit" color="primary" disabled={loading}>
            {loading ? <CircularProgress size={24} /> : "Criar"}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ScheduledBroadcastModal;
